import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CTASection = () => {
  const contactOptions = [
    {
      icon: 'Calendar',
      title: 'Executive Briefing',
      description: 'Schedule a personalized demo with our enterprise team',
      action: 'Schedule Now',
      link: '/request-demo',
      color: '#1A73E8'
    },
    {
      icon: 'Phone',
      title: 'Call Sales',
      description: 'Speak directly with our enterprise solutions experts',
      action: 'Call: 1-800-POLICY-1',
      link: 'tel:1-800-765-4291',
      color: '#10B981'
    },
    {
      icon: 'Mail',
      title: 'Email Us',
      description: 'Send your requirements and we\'ll respond within 24 hours',
      action: 'enterprise@policyalerts.com',
      link: 'mailto:enterprise@policyalerts.com',
      color: '#0066FF'
    }
  ];

  return (
    <section className="bg-[#FAFBFC] py-12 md:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-[#1B1B20] to-[#1A73E8]/20 rounded-3xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="p-8 md:p-10 lg:p-12 text-white">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-6">
                <Icon name="Sparkles" size={16} color="#FFFFFF" />
                <span className="text-xs md:text-sm font-medium">Get Started Today</span>
              </div>

              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 leading-tight">
                Ready to Transform Your Policy Monitoring Operations?
              </h2>
              
              <p className="text-base md:text-lg text-gray-300 mb-8 leading-relaxed">
                Join Stryker, Medtronic, Boston Scientific, and other Fortune 500 leaders who trust PolicyAlerts for enterprise-grade policy intelligence. Get started with a personalized consultation.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#10B981]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Check" size={14} color="#10B981" />
                  </div>
                  <span className="text-sm md:text-base">Dedicated enterprise account management</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#10B981]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Check" size={14} color="#10B981" />
                  </div>
                  <span className="text-sm md:text-base">Custom implementation and integration support</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#10B981]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Check" size={14} color="#10B981" />
                  </div>
                  <span className="text-sm md:text-base">99.9% uptime SLA with 24/7 support</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#10B981]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Check" size={14} color="#10B981" />
                  </div>
                  <span className="text-sm md:text-base">SOC 2 Type II and HIPAA compliance included</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  variant="default"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="bg-[#ED1C24] hover:bg-[#ED1C24]/90"
                  asChild
                >
                  <Link to="/request-demo">Request Enterprise Demo</Link>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Download"
                  iconPosition="left"
                  className="border-white/30 text-white hover:bg-white/10"
                >
                  Download Brochure
                </Button>
              </div>
            </div>

            <div className="bg-white p-8 md:p-10 lg:p-12">
              <h3 className="text-xl md:text-2xl font-bold text-[#1B1B20] mb-6">
                Contact Our Enterprise Team
              </h3>

              <div className="space-y-6">
                {contactOptions?.map((option, index) => (
                  <div key={index} className="bg-[#FAFBFC] rounded-xl p-6 hover:shadow-md transition-all border border-[#E5E7EB]">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${option?.color}20` }}>
                        <Icon name={option?.icon} size={24} color={option?.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base md:text-lg font-semibold text-[#1B1B20] mb-1">
                          {option?.title}
                        </h4>
                        <p className="text-sm text-[#6B7280] mb-3">
                          {option?.description}
                        </p>
                        {option?.link?.startsWith('/') ? (
                          <Link
                            to={option?.link}
                            className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
                            style={{ color: option?.color }}
                          >
                            {option?.action}
                            <Icon name="ArrowRight" size={14} color={option?.color} />
                          </Link>
                        ) : (
                          <a
                            href={option?.link}
                            className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
                            style={{ color: option?.color }}
                          >
                            {option?.action}
                            <Icon name="ExternalLink" size={14} color={option?.color} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-[#1A73E8]/10 border border-[#1A73E8]/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <Icon name="Clock" size={20} color="#1A73E8" />
                  <div>
                    <h5 className="text-sm font-semibold text-[#1B1B20] mb-1">
                      Response Time Guarantee
                    </h5>
                    <p className="text-xs text-[#6B7280]">
                      Enterprise inquiries receive priority response within 4 business hours
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-[#1A73E8] mb-2">13+</div>
            <div className="text-sm text-[#6B7280]">Years of Service</div>
          </div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-[#1A73E8] mb-2">50+</div>
            <div className="text-sm text-[#6B7280]">Fortune 500 Clients</div>
          </div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-[#1A73E8] mb-2">500K+</div>
            <div className="text-sm text-[#6B7280]">Policies Monitored</div>
          </div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-[#1A73E8] mb-2">99.9%</div>
            <div className="text-sm text-[#6B7280]">Uptime SLA</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;